import React from 'react';
import PropTypes from "prop-types";
import {TreeSelect} from "antd"
import Text from 'antd/lib/typography/Text';
import I18n from '../../common/I18n';
const { TreeNode } = TreeSelect;

function AccountCodesFilter (props) {
  const { SelectedAccountCodes, onValueChange , accounts, size="small", element = 'accountCodes', valueField="id", styleObject={}, showLabel = false, loading = false, autoClearSearchValue = false, resetSearchToken, disabled = false } = props;

  const [searchValue, setSearchValue] = React.useState("");

  // Clear the typed search text whenever the parent bumps resetSearchToken
  // (e.g. on the filter panel's "Search" button click).
  React.useEffect(() => {
    setSearchValue("");
  }, [resetSearchToken]);

  // Same title match the dropdown uses (title = "name (code)") so the ids we
  // compute here are exactly the rows currently visible under the search.
  const getFilteredIds = (text) => {
    const q = (text || "").toLowerCase();
    return accounts
      .filter((account) =>
        `${account.name} (${account.code})`.toLowerCase().indexOf(q) >= 0
      )
      .map((account) => account[valueField]);
  };

  const handleSearch = (value) => {
    setSearchValue(value);
  };

  const handleChange = (value, _label, extra) => {
    const triggerValue = extra && extra.triggerValue;
    const searching = !!(searchValue && searchValue.trim().length > 0);

    // When "All" is ticked while a search is active, antd would select every
    // account (the hidden ones too). Instead, scope "All" to the matching rows:
    // add them to the current selection (or remove them if they were all
    // already selected — mirrors the checkbox's tri-state).
    if (triggerValue === "ALL" && searching) {
      const current = SelectedAccountCodes || [];
      const filteredIds = getFilteredIds(searchValue);
      const allAlreadySelected =
        filteredIds.length > 0 && filteredIds.every((id) => current.includes(id));

      let next;
      if (allAlreadySelected) {
        next = current.filter((id) => !filteredIds.includes(id));
      } else {
        next = Array.from(new Set([...current, ...filteredIds]));
      }
      onValueChange(element, next);
      if (autoClearSearchValue || next.length === 0) setSearchValue("");
      return;
    }

    onValueChange(element, value);
    // Clearing the selection (field ✕ / unchecking the last item) also clears
    // the leftover typed search text.
    if (autoClearSearchValue || !value || value.length === 0) setSearchValue("");
  };

  return (
    <div className="AccountCodesFilterContainer" data-testid="account-filter-dropdown">
      {showLabel && <Text className="marginRight5 textBold">{I18n.t("general.account")}:</Text>}
      <TreeSelect
        value={ SelectedAccountCodes }
        style={ { width: '100%', ...styleObject } }
        showSearch
        searchValue={ searchValue }
        onSearch={ handleSearch }
        size={size || "small"}
        className="minWidth"
        dropdownStyle={ {
          maxHeight: 300,
          overflow: "auto",
        } }
        placeholder="--Select--"
        allowClear={!loading && !disabled}
        autoClearSearchValue={autoClearSearchValue}
        treeDefaultExpandAll
        dropdownMatchSelectWidth={ false }
        onChange={ handleChange }
        treeCheckable
        maxTagCount={ 1 }
        treeNodeFilterProp="title"
        disabled={loading || disabled}
        filterOption={(input, option) =>
          option.props.children
            .toLowerCase()
            .indexOf(input.toLowerCase()) >= 0
        }
      >
        <TreeNode value="ALL" title="All" key="0-1">
          { accounts.map((account) => (
            <TreeNode
              value={ account[valueField] }
              title={ `${account.name} (${account.code})` }
              key={ account.id }
            />
          )) }
        </TreeNode>
      </TreeSelect>
    </div>
  );
}

export default AccountCodesFilter;

AccountCodesFilter.propTypes = {
  SelectedAccountCodes: PropTypes.array,
  onValueChange: PropTypes.func,
  accounts: PropTypes.array,
  loading: PropTypes.bool,
  autoClearSearchValue: PropTypes.bool,
  resetSearchToken: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  disabled: PropTypes.bool,
};

AccountCodesFilter.defaultProps = {
  SelectedAccountCodes: [],
  onValueChange: () => {},
  accounts: [],
  loading: false,
  autoClearSearchValue: false,
};
